import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Banner,
  Button,
  Card,
  Checkbox,
  EmptyState,
  Input,
  PageContainer,
  PageHeader,
  Select,
  Spinner,
  Textarea,
  useToast,
} from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useProperty } from '../../app/PropertyContext';
import { useT } from '../../i18n';
import { stockCountPath } from '../../lib/slug';
import { catalogApi } from '../catalog/catalogApi';
import { countsApi, COUNT_TYPES } from './countsApi';
import '../requisitions/requisitions.css';

const todayIso = () => new Date().toISOString().slice(0, 10);

export default function CountFormPage() {
  const navigate = useNavigate();
  const t = useT();
  const toast = useToast();
  const { activePropertyId, hasPermission } = useProperty();
  const canCount = hasPermission('inventory.count');

  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [locationId, setLocationId] = useState('');
  const [countType, setCountType] = useState('Full');
  const [countDate, setCountDate] = useState(todayIso());
  const [includeZero, setIncludeZero] = useState(true);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    let active = true;
    catalogApi.listLocations(activePropertyId)
      .then((res) => { if (active) { setLocations(res || []); if ((res || []).length === 1) setLocationId(res[0].id); } })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [activePropertyId]);

  const locationOptions = useMemo(
    () => [{ value: '', label: t('Select a location…') }, ...locations.map((l) => ({ value: l.id, label: l.name }))],
    [locations, t],
  );

  const create = async () => {
    if (!locationId) { toast.error(t('Choose a location to count')); return; }
    setBusy(true);
    try {
      const count = await countsApi.create({
        locationId,
        countType,
        countDate: countDate ? new Date(countDate).toISOString() : null,
        includeZero,
        notes: notes.trim() || null,
      });
      toast.success(t('Count sheet created — record your counts'));
      navigate(stockCountPath(count));
    } catch (err) {
      toast.error(t('Could not create count'), { description: err?.message });
      setBusy(false);
    }
  };

  if (!canCount) {
    return (
      <PageContainer size="md">
        <EmptyState icon={<Icon.Lock size={28} />} title={t('Not allowed')} description={t("You don't have permission to perform stock counts.")} />
      </PageContainer>
    );
  }

  return (
    <PageContainer size="md">
      <Button variant="ghost" size="sm" leadingIcon={<Icon.ArrowLeft size={15} />} onClick={() => navigate('/counts')} style={{ alignSelf: 'flex-start', marginBottom: 'var(--space-3)' }}>
        {t('Stock counts')}
      </Button>

      <PageHeader
        title={t('New stock count')}
        description={t('Open a count sheet for one location. The current on-hand of every item in scope is snapshotted so you can record the physical counts against it.')}
        icon={<Icon.ClipboardList size={22} />}
      />

      {loading ? (
        <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-16)' }}><Spinner size={26} /></div>
      ) : locations.length === 0 ? (
        <Banner tone="warning">{t('No stock locations exist yet. Create a location in Settings before starting a count.')}</Banner>
      ) : (
        <Card>
          <Card.Body>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
              <div className="ps-field">
                <span className="ps-field__label">{t('Location')}</span>
                <Select value={locationId} onChange={setLocationId} ariaLabel={t('Location')} options={locationOptions} searchable />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'var(--space-3)' }}>
                <div className="ps-field">
                  <span className="ps-field__label">{t('Count type')}</span>
                  <Select value={countType} onChange={setCountType} ariaLabel={t('Count type')} options={COUNT_TYPES.map((c) => ({ ...c, label: t(c.label) }))} />
                </div>
                <Input label={t('Count date')} type="date" value={countDate} onChange={(e) => setCountDate(e.target.value)} />
              </div>

              <Checkbox
                checked={includeZero}
                onChange={(e) => setIncludeZero(e?.target ? e.target.checked : e)}
                label={t('Include items with zero on-hand')}
                hint={t('Useful for a full count so missing items can be reconciled; uncheck to count only items that currently show stock.')}
              />

              <Textarea label={t('Notes')} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t('Optional — e.g. month-end count, area, counter name')} />

              <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                <Button variant="primary" loading={busy} leadingIcon={<Icon.ClipboardList size={16} />} onClick={create}>
                  {t('Create count sheet')}
                </Button>
                <Button variant="ghost" onClick={() => navigate('/counts')} disabled={busy}>{t('Cancel')}</Button>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}
    </PageContainer>
  );
}
